import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { AppInputField, AppPromptsField, AppSelectField } from '@/form/FormUI';
import type { NodeFormProps, RoomNodeType } from '@/nodes';
import { useSavedFormValues } from '@/state';
import { extractFieldErrors } from '@/util/extractFieldErrors';
import { zodResolver } from '@hookform/resolvers/zod';
import { RoomDataSchema, type RoomDataType, RoomGroups, RoomTypeLabels, type RoomType } from '@rock/types';
import { type FC, useCallback, useEffect } from 'react';
import { type FieldErrors, useForm, useWatch } from 'react-hook-form';
import { toast } from 'sonner';
import type { z } from 'zod';

type FormInput = z.input<typeof RoomDataSchema>;

const RoomForm: FC<NodeFormProps<RoomNodeType>> = ({ flowId, node, updateNode, cancelUpdate }) => {
    const [formState, setFormState] = useSavedFormValues<FormInput>({ flowId, nodeId: node.id });
    const form = useForm<FormInput, unknown, RoomDataType>({
        resolver: zodResolver(RoomDataSchema),
        defaultValues: formState || node.data,
    });

    const watchedFields = useWatch({ control: form.control });
    useEffect(() => {
        setFormState(watchedFields);
    }, [setFormState, watchedFields]);

    const onSubmit = useCallback(
        (values: RoomDataType) => {
            setFormState(null);
            updateNode({
                ...node,
                data: values,
            });
        },
        [setFormState, node, updateNode],
    );

    const onInvalid = useCallback((errors: FieldErrors) => {
        toast.error('Validation Errors', {
            description: extractFieldErrors(errors).join(', '),
        });
    }, []);

    const onCancel = useCallback(() => {
        setFormState(null);
        cancelUpdate();
    }, [setFormState, cancelUpdate]);

    return (
        <Form {...form}>
            <form
                onSubmit={form.handleSubmit(onSubmit, onInvalid)}
                className="flex flex-1 flex-col gap-8 items-stretch overflow-hidden"
            >
                <div className="flex flex-1 flex-col gap-8 items-stretch p-5 overflow-scroll">
                    <AppInputField
                        label="Page Title"
                        placeholder="Page Title"
                        control={form.control}
                        name="pageTitle"
                        description="The title displayed in the navigation header."
                    />

                    <div className="flex flex-col gap-2">
                        <AppSelectField
                            control={form.control}
                            name="roomGroupHint"
                            label="Assumed Room Group"
                            placeholder="Select a room type group"
                            description="If provided, the guide will pre-filter the room list to this group."
                            options={RoomGroups.map((group) => ({ value: group.id, label: group.label }))}
                        />
                        {watchedFields.roomGroupHint && (
                            <div className="flex flex-row justify-end">
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => form.setValue('roomGroupHint', undefined)}
                                >
                                    Clear Assumed Room Group
                                </Button>
                            </div>
                        )}
                    </div>

                    <div className="flex flex-col gap-2">
                        <AppSelectField
                            control={form.control}
                            name="autoSelectRoomType"
                            label="Auto-Select Room of Type"
                            placeholder="Select a room type"
                            description="If set, and exactly one room of this type exists, the room will be auto-selected and this screen will be skipped. If zero or more than one room matches, the selection screen will still be shown."
                            options={(Object.entries(RoomTypeLabels) as [RoomType, string][]).map(([value, label]) => ({
                                value,
                                label,
                            }))}
                        />
                        {watchedFields.autoSelectRoomType && (
                            <div className="flex flex-row justify-end">
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => form.setValue('autoSelectRoomType', undefined)}
                                >
                                    Clear Auto-Select Room Type
                                </Button>
                            </div>
                        )}
                    </div>

                    <AppPromptsField description="What text to show above Room Selection?" />
                </div>

                <div className="flex flex-row gap-3 p-5">
                    <Button type="submit" className="flex-1">
                        Save Details
                    </Button>
                    <Button type="button" className="flex-1" variant="outline" onClick={onCancel}>
                        Cancel
                    </Button>
                </div>
            </form>
        </Form>
    );
};

export default RoomForm;
